import { createContext, useContext, useState } from 'react';
import {
  IBadgeContextType,
  IBadgeDetail,
  IBadgeIuuseData,
  IBadgeTemplate,
  IUserBadgesData,
  TCreateBadgeTemplate,
  TDeleteBadgeTemplate,
  TDropsBadge,
  TExpireBadgeIssue,
  TFetchBadgeDetail,
  TFetchBadgeIusseList,
  TFetchBadgeStatistics,
  TFetchBadgeTemplates,
  TFetchUserBadgeList,
  TResetBadgeTemplate,
  TSearchBadgeTemplate,
  TUpdateBadgeTemplate,
} from './types';
import { useError } from '@hook/useError';
import { CLIENT_API } from '@api/services';

const BadgeContext = createContext<IBadgeContextType | undefined>(undefined);

export function BadgeContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  /**
   * hooks
   */
  const { handleError } = useError();

  /**
   * states
   */
  const [badgeTemplates, setBadgeTemplates] = useState<IBadgeTemplate[]>([]);
  const [totalBadgeTemplatesCounts, setTotalBadgeTemplatesCounts] = useState(0);
  const [totalBadgeTemplatesPages, setTotalBadgeTemplatesPages] = useState(1);
  const [badgeStatistics, setBadgeStatistics] = useState<number[]>([]);
  const [badgeIssusData, setBadgeIssusData] = useState<IBadgeIuuseData | null>(
    null,
  );
  const [userBadgesData, setUserBadgesData] = useState<IUserBadgesData | null>(
    null,
  );
  const [badgeDetail, setBadgeDetail] = useState<IBadgeDetail | null>(null);

  /**
   * Functions
   */
  const fetchBadgeTemplates: TFetchBadgeTemplates = async ({
    requestPageNumber = 1,
    filter,
    search,
    creatorId,
    limit,
  }) => {
    try {
      const {
        data: { data, totalElementCount, totalPage },
      } = await CLIENT_API.SbtTemplateApi.getAllSbtTemplateByOffset({
        creatorId,
        limit,
        requestPageNumber,
        activeType: filter,
        ...(search && { search }),
      });

      setBadgeTemplates([...data]);
      setTotalBadgeTemplatesCounts(totalElementCount);
      setTotalBadgeTemplatesPages(totalPage);
    } catch (e) {
      handleError(e);
    }
  };

  const searchBadgeTemplate: TSearchBadgeTemplate = (keyword) => {
    console.log(keyword);
  };

  const resetBadgeTemplate: TResetBadgeTemplate = () => {};

  const createBadgeTemplate: TCreateBadgeTemplate = async (parmas) => {
    try {
      const {
        data: { id },
      } = await CLIENT_API.SbtTemplateApi.createSbtTemplate(parmas);
      if (id) return id;
    } catch (e) {
      handleError(e);
    }
  };

  const updateBadgeTemplate: TUpdateBadgeTemplate = async ({
    templateId,
    data,
  }) => {
    try {
      const {
        data: { id },
      } = await CLIENT_API.SbtTemplateApi.updateSbtTemplate(templateId, data);

      if (id) {
        const updated = badgeTemplates.map((item) =>
          item.id === id
            ? { ...item, ...data, isTemplateActive: data.isActive }
            : { ...item },
        );

        setBadgeTemplates([...updated]);
        return id;
      }
    } catch (e) {
      handleError(e);
    }
  };

  const deleteBadgeTemplate: TDeleteBadgeTemplate = async (templateId) => {
    try {
      const { status } =
        await CLIENT_API.SbtTemplateApi.deleteSbtTemplate(templateId);

      if (status === 200) {
        const updated = badgeTemplates.filter((item) => item.id !== templateId);

        setBadgeTemplates([...updated]);
        return true;
      }
    } catch (e) {
      handleError(e);
    }
  };

  const fetchBadgeStatistics: TFetchBadgeStatistics = async ({
    userId,
    duration,
  }) => {
    try {
      const {
        data: { data },
      } = await CLIENT_API.SbtApi.getSbtsStatistics(userId, {
        duration,
      });

      const dataArray = Object.values(data);
      setBadgeStatistics(dataArray);
    } catch (e) {
      handleError(e);
    }
  };

  const fetchBadgeIssueList: TFetchBadgeIusseList = async ({
    userId,
    sbtStatusType,
    requestPageNumber,
    search,
  }) => {
    try {
      const { data } = await CLIENT_API.SbtApi.getAllSbtForIssuer(userId, {
        sortField: 'RECENT',
        sbtStatusType,
        requestPageNumber,
        ...(search && { search, searchType: 'BADGE' }),
      });
      setBadgeIssusData(data);
    } catch (e) {
      handleError(e);
    }
  };

  const expireBadgeIssue: TExpireBadgeIssue = async (badgeId) => {
    try {
      const { status } = await CLIENT_API.SbtApi.expireSbt(badgeId);
      if (status === 200) {
        if (!badgeIssusData) return;

        setBadgeIssusData({
          ...badgeIssusData,
          data: badgeIssusData.data.map((item) =>
            item.id === badgeId ? { ...item, isActive: false } : { ...item },
          ),
        });
      }
    } catch (e) {
      handleError(e);
    }
  };

  const dropBadge: TDropsBadge = async (params) => {
    try {
      const {
        data: { id },
      } = await CLIENT_API.SbtApi.provideSbt(params);
      if (id) {
        return id;
      }
    } catch (e: any) {
      const {
        response: {
          data: { errorCode },
        },
      } = e;
      handleError(e);
      throw Error(errorCode);
    }
  };

  const fetchUserBadgeList: TFetchUserBadgeList = async ({
    userId,
    requestPageNumber,
    limit,
    creatorId,
  }) => {
    try {
      const { data } = await CLIENT_API.SbtApi.getAllSbtForTemplateOwner(
        userId,
        {
          creatorId,
          requestPageNumber,
          limit,
          sortField: 'RECENT',
        },
      );

      if (data) {
        setUserBadgesData(data);
      }
    } catch (e) {
      handleError(e);
    }
  };

  const fetchBadgeDetail: TFetchBadgeDetail = async (badgeId) => {
    try {
      const { data } = await CLIENT_API.SbtApi.getSbtById(badgeId);
      if (data) {
        setBadgeDetail(data);
      }
    } catch (e) {
      handleError(e);
    }
  };

  const resetBadgeDetail = () => {
    setBadgeDetail(null);
  };

  return (
    <BadgeContext.Provider
      value={{
        badgeTemplates,
        fetchBadgeTemplates,
        totalBadgeTemplatesCounts,
        totalBadgeTemplatesPages,
        searchBadgeTemplate,
        resetBadgeTemplate,
        createBadgeTemplate,
        updateBadgeTemplate,
        deleteBadgeTemplate,
        fetchBadgeStatistics,
        badgeStatistics,
        fetchBadgeIssueList,
        badgeIssusData,
        expireBadgeIssue,
        dropBadge,
        fetchUserBadgeList,
        userBadgesData,
        badgeDetail,
        fetchBadgeDetail,
        resetBadgeDetail,
      }}
    >
      {children}
    </BadgeContext.Provider>
  );
}

export const useBadgeContext = () => {
  const context = useContext(BadgeContext);
  if (!context) {
    throw new Error(
      'useBadgeContext must be used within a BadgeContextProvider',
    );
  }
  return context;
};
